/**
 * Mapa de fallback PT-PT para quando os ficheiros JSON de tradução
 * não estão disponíveis (ex: erro de rede, API indisponível).
 *
 * Garante que as mensagens mais comuns são sempre apresentadas
 * em Português de Portugal, em vez de exibir chaves técnicas
 * como "COMMON.ALERTS.ATTENTION" ou "ERRORS.REQUEST_FAILED".
 *
 * IMPORTANTE: Manter sincronizado com assets/i18n/pt/all.json
 */
export const PT_FALLBACK: Record<string, string> = {
  // ── COMMON ────────────────────────────────────────────────
  'COMMON.ALERTS.ATTENTION': 'Atenção',
  'COMMON.ALERTS.SUCCESS': 'Pedido Registado',
  'COMMON.ALERTS.MISSING_DATA': 'Dados em falta',
  'COMMON.ALERTS.SAVE_SUCCESS': 'Guardado com sucesso.',
  'COMMON.ALERTS.CONFIRM': 'Confirmar',
  'COMMON.ALERTS.INFO': 'Informação',
  'COMMON.SAVE': 'Guardar',
  'COMMON.SEARCH': 'Pesquisar...',
  'COMMON.INACTIVE': 'Inativo',
  'COMMON.BUTTONS.CANCEL': 'Cancelar',
  'COMMON.BUTTONS.REMOVE': 'Remover',
  'COMMON.BUTTONS.YES_DELETE': 'Sim, eliminar',
  'COMMON.BUTTONS.CLOSE': 'Fechar',
  'COMMON.LOADING.DATA': 'A carregar dados...',
  'COMMON.LOADING.SPINNER_TITLE': '',
  'COMMON.LOADING.SPINNER_SUBTITLE': '',
  'COMMON.SUCCESS.REMOVED_SUCCESSFULLY': 'Removido com sucesso.',
  'COMMON.SUCCESS.SAVED_SUCCESSFULLY': 'Guardado com sucesso.',
  'COMMON.SUCCESS.SENT_SUCCESSFULLY': 'Enviado com sucesso.',
  'COMMON.SENDING': 'A enviar...',
  'COMMON.REFRESHING': 'A actualizar...',
  'COMMON.ERRORS.INVALID_EMAIL': 'Email inválido',
  'COMMON.ERRORS.VALID_EMAIL_REQUIRED': 'Por favor, introduza um endereço de e-mail válido.',

  // ── ERRORS ────────────────────────────────────────────────
  'ERRORS.SUBMIT_FAILED': 'Infelizmente ocorreu um erro ao submeter o seu pedido. Por favor, tente novamente.',
  'ERRORS.UNEXPECTED': 'Não foi possível concluir a operação. Por favor, tente novamente.',
  'ERRORS.NOT_FOUND': 'Recurso não encontrado.',
  'ERRORS.FORBIDDEN': 'Sem permissões para efectuar esta ação.',
  'ERRORS.OFFLINE': 'Sem ligação à internet.',
  'ERRORS.NO_INTERNET': 'Verifique, por favor, a ligação à internet',
  'ERRORS.TIMEOUT': 'A ligação demorou mais que o esperado. Por favor, tente novamente.',
  'ERRORS.UNAUTHORIZED': 'Sessão expirada ou não autorizada.',
  'ERRORS.BAD_REQUEST': 'Dados inválidos ou incompletos. Por favor, reveja a sua solicitação.',
  'ERRORS.REQUEST_FAILED': 'Não foi possível processar o seu pedido! Por favor, tente mais tarde.',
  'ERRORS.REQUEST_LATER': 'Não foi possível processar o seu pedido! Por favor, tente mais tarde.',
  'ERRORS.PAYLOAD_TOO_LARGE':
    'O pedido contém dados demasiado grandes. Por favor, reduza o tamanho dos ficheiros ou contacte o suporte.',
  'ERRORS.INTERNAL': 'O serviço encontra-se momentaneamente indisponível. Por favor, tente mais tarde.',
  'ERRORS.UNAVAILABLE': 'O serviço está temporariamente indisponível. Tente novamente mais tarde.',
  'ERRORS.UNKNOWN': 'Não conseguimos processar o pedido. Se persistir, contacte o nosso apoio.',
  'ERRORS.MISSING_DATA': 'Dados em falta!',
  'ERRORS.INVALID_FILENAME': 'Nome de ficheiro inválido.',
  'ERRORS.INVALID_DOCUMENT': 'Documento inválido!',
  'ERRORS.LINK_NO_LONGER_VALID': 'Este link já não está válido.',
  'ERRORS.INVALID_EMAIL': 'O e-mail introduzido é inválido.',
  'ERRORS.VALID_EMAIL_REQUIRED': 'Por favor, introduza um endereço de e-mail válido.',
  'ERRORS.INCOMPLETE_DATA': 'Dados incompletos. Por favor verifique.',
  'ERRORS.INVALID_NIF': 'NIF inválido.',
  'ERRORS.NO_DOCUMENTS': 'Nenhum documento encontrado.',
  'ERRORS.TICKET_FAILED': 'Falha ao criar o pedido de suporte.',
  'ERRORS.NIB_REGISTERED': 'Este NIB já se encontra registado.',
  'ERRORS.FILE_UNKNOWN_DAMAGED': 'Ficheiro desconhecido ou danificado.',
  'ERRORS.PDF_LOAD_FAILED': 'Falha ao carregar o PDF.',
  'ERRORS.SAVE_FAILED': 'Falha ao gravar os dados.',
  'ERRORS.INVALID_BIRTHDATE': 'Data de nascimento inválida.',
  'ERRORS.INCOMPLETE_DATA_FIELDS': 'Por favor, preencha todos os campos obrigatórios.',
  'ERRORS.INVALID_IMAGE': 'Formato de imagem inválido. Use JPG, PNG ou WebP.',
  'ERRORS.IMAGE_PROCESSING': 'Erro ao processar a imagem.',
  'ERRORS.NAME_REQUIRED': 'O nome da companhia é obrigatório.',

  // ── ACTIONS ───────────────────────────────────────────────
  'ACTIONS.ADD': 'Adicionar',
  'ACTIONS.EDIT': 'Alterar',
  'ACTIONS.SAVE': 'Guardar',
  'ACTIONS.BACK': 'Voltar',
  'ACTIONS.NEXT': 'Seguinte',
  'ACTIONS.DELETE': 'Apagar',
  'ACTIONS.ATTACH': 'Anexos',
  'ACTIONS.DOWNLOAD': 'Download',
  'ACTIONS.SHARE': 'Partilhar',
  'ACTIONS.VIEW': 'Ver',
  'ACTIONS.CLOSE': 'Fechar',
  'ACTIONS.FILTER': 'Filtrar',
  'ACTIONS.CLEAR': 'Limpar',
  'ACTIONS.YES': 'Sim',
  'ACTIONS.NO': 'Não',
  'ACTIONS.OK': 'OK',
  'ACTIONS.CANCEL': 'Cancelar',
  'ACTIONS.ALL': 'Tudo',
  'ACTIONS.SEARCH': 'Pesquisar',
  'ACTIONS.REFRESH': 'Atualizar',

  // ── ALERTS ────────────────────────────────────────────────
  'ALERTS.CONFIRM': 'Confirmar',
  'ALERTS.CONFIRM_DELETE': 'Tem a certeza que deseja eliminar este item?',

  // ── FORM ──────────────────────────────────────────────────
  'FORM.REQUIRED': 'Preenchimento obrigatório',

  // ── TABLE ─────────────────────────────────────────────────
  'TABLE.NO_DATA': 'Sem dados para apresentar',

  // ── DOCUMENTS ─────────────────────────────────────────────
  'DOCUMENTS.DOCUMENT': 'Documento',
  'DOCUMENTS.FILE': 'Ficheiro',
  'DOCUMENTS.FILES': 'Ficheiros',
  'DOCUMENTS.DOWNLOAD_ALL': 'Descarregar todos',
  'DOCUMENTS.DOWNLOAD_ALL_ZIP': 'Descarregar todos (ZIP)',
  'DOCUMENTS.DOWNLOAD_SELECTED': 'Descarregar selecionados',
  'DOCUMENTS.DOWNLOAD_SELECTED_ZIP': 'Descarregar selecionados (ZIP)',
  'DOCUMENTS.DOWNLOADING_ZIP': 'A gerar arquivo ZIP...',
  'DOCUMENTS.ATTACH': 'Anexar',
  'DOCUMENTS.TAKE_PHOTO': 'Tirar foto',
  'DOCUMENTS.SHARE_SELECTED': 'Partilhar selecionados',
  'DOCUMENTS.SHARE': 'Partilhar',

  // ── AI_DROPZONE ───────────────────────────────────────────
  'AI_DROPZONE.TITLE': 'Reconhecimento de Documentos',
  'AI_DROPZONE.SUBTITLE': 'Ricardo IA',
  'AI_DROPZONE.DOC_COUNT_SINGLE': '{{count}} documento',
  'AI_DROPZONE.DOC_COUNT_MULTI': '{{count}} documentos',
  'AI_DROPZONE.STEP_VALIDATE': 'Validação e integridade dos ficheiros',
  'AI_DROPZONE.STEP_ENCODE': 'Preparação e otimização local',
  'AI_DROPZONE.STEP_SEND': 'Envio seguro para o motor de IA',
  'AI_DROPZONE.STEP_READ': 'Leitura inteligente e extração de dados',
  'AI_DROPZONE.STEP_MATCH': 'Cruzamento com Clientes e Apólices',
  'AI_DROPZONE.STEP_FINISH': 'Conclusão e abertura do assistente',
  'AI_DROPZONE.STATUS_VALIDATING': 'A verificar formatos e integridade dos ficheiros...',
  'AI_DROPZONE.STATUS_VALIDATED': '{{count}} ficheiro(s) validados com sucesso.',
  'AI_DROPZONE.STATUS_ENCODING': 'A preparar documento {{current}} de {{total}}: {{filename}}',
  'AI_DROPZONE.STATUS_ENCODED': '{{count}} ficheiro(s) preparados ({{mb}} MB).',
  'AI_DROPZONE.STATUS_SENDING': 'A estabelecer ligação segura e a transmitir...',
  'AI_DROPZONE.STATUS_READING': 'O modelo de visão IA está a analisar o conteúdo...',
  'AI_DROPZONE.STATUS_MATCHING': 'A reconciliar dados com a base de dados de Clientes e Apólices...',
  'AI_DROPZONE.STATUS_FINISHED': 'Análise concluída com sucesso! A preparar importação...',
  'AI_DROPZONE.STATUS_ERROR': 'Ocorreu um erro durante a análise dos documentos.',
  'AI_DROPZONE.ELAPSED': 'Tempo:',
  'AI_DROPZONE.MODEL_HINT': 'Visão Computacional GPT-4o',
};
